package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onChangeFunction
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.entities.Group
import nl.volleyplanner.api.planner.TaskDay
import nl.volleyplanner.api.planner.TaskDaysResponse
import nl.volleyplanner.api.sports.MatchEvent
import nl.volleyplanner.extensions.today
import nl.volleyplanner.nevoboservice.extensions.toSlug
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.base.routing.PageMountContext
import nl.volleyplanner.web.service.OrganisationContext
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSelectElement


data class ThuisprogrammaControls(
    val editMode: Boolean,
    val statisticsMode: Boolean,
    val plannerRunning: Boolean = false,
    val showLastPlannerResult: Boolean = false,
    val showChangesWithOtherVersion: Boolean = false,
    val showChangesToVersionSlug: String? = null
)

data class ThuisprogrammaFilters(
    val datesToShow: String?,
    val team: String?,
    val defaultDateFilter: String = "future"
) {
    val datesToShowFilterValue = datesToShow ?: defaultDateFilter
    val today = LocalDate.today()

    fun filterTaskDaysToShow(taskDays: List<TaskDay>): List<TaskDay> {
        val filtered = taskDays.filter { taskDay ->
            when (datesToShowFilterValue) {
                "future" -> taskDay.date >= today
                "all" -> true
                else -> datesToShow == taskDay.date.format(DATE_FORMAT_ISO)
            }
        }
        if (datesToShow == defaultDateFilter && filtered.isEmpty()) {
            return taskDays
        }
        return filtered
    }

    fun getDatesToShowLabel() : String {
        return when {
            datesToShow == null -> "Toekomstige schema"
            datesToShow == "all" -> "Volledige schema"
            else -> datesToShow
        }
    }

    fun showMatch(teamToCheck: Group?, matchEvent: MatchEvent) : Boolean {
        if (teamToCheck == null) return true

        // FIXME: Currently checking teamHome.contains() and teamGuest.contains() because the match team
        //  can include a sponsor name, where the group team name will only contain the club name
        //  need a better way of linking groups to teams and providing such information with a Match
        return matchEvent.match.teamHome.contains(teamToCheck.name) || matchEvent.match.teamGuest.contains(teamToCheck.name) ||
                matchEvent.refereeTasks.any { teamToCheck.memberIds!!.contains(it.assignee.personAssignee?.id) } ||
                matchEvent.scoreKeeperTasks.any { teamToCheck.memberIds!!.contains(it.assignee.personAssignee?.id) }
    }
}

class ThuisprogrammaComponent(
    val problemSlug: String,
    val problemVersionSlug: String? = null,
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager
    ) : HTMLComponent() {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaComponent")
    }

    val taskDaysDataState: ApiDataStateFlow<TaskDaysResponse, *> get() = thuisprogrammaDataManager.problemData.getVersionData(problemVersionSlug).taskDaysState
    val taskAssigneeStoreState
        get() = thuisprogrammaDataManager.problemData.getVersionData(problemVersionSlug).assigneeStoresState
    val filtersState: StateFlow<ThuisprogrammaFilters> get() = thuisprogrammaDataManager.thuisprogrammaFiltersState

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val thuisprogrammaFilters = filtersState.value
        val thuisprogrammaData = taskDaysDataState.value

        val teamsBySlug = thuisprogrammaDataManager.problemData.groupsDataFlow.value
            ?.associateBy { it.name.toSlug() } ?: emptyMap()
        val teamToFilter = teamsBySlug[thuisprogrammaFilters.team]

        return consumer.div {
            logger.debug("[$problemVersionSlug@$componentId] thuisprogrammaData: ${thuisprogrammaData?.taskDays?.size}, " +
                    "groups: ${thuisprogrammaDataManager.problemData.groupsDataFlow.value?.size}, " +
                    "assigneeStores: ${taskAssigneeStoreState.value?.size}")
            val taskAssigneeStoreData = taskAssigneeStoreState.value
            if (thuisprogrammaData == null || thuisprogrammaDataManager.problemData.groupsDataFlow.value == null || taskAssigneeStoreData == null) {
                spinner()
            } else {
                val taskDays = thuisprogrammaData.taskDays
                thuisprogrammaDataManager.problemData.groupsDataFlow.value
                val teamById = thuisprogrammaDataManager.problemData.groupsDataFlow.value!!.associateBy { it.id }
                component(
                    Toolbar(listOf(
                    ToolbarItem(
                        itemHtmlId = "tp-date-toolbar-item",
                        buttonContent = {
                            icon("bi-calendar-date")
                            +"Datum"
                        },
                        itemContent = {
                            select(classes = "form-select") {
                                id = "tp-filter-date-select"
                                onChangeFunction = { event ->
                                    val value = (event.currentTarget as HTMLSelectElement).value

                                    val queryParamValue = when (value) {
                                        "future" -> null
                                        else -> value
                                    }
                                    NavigationManager.setQueryParameters(mapOf("datum" to queryParamValue))
                                }
                                option {
                                    selected = thuisprogrammaFilters.datesToShowFilterValue == "all"

                                    value = "all"
                                    +"Volledige schema"
                                }
                                option {
                                    selected = thuisprogrammaFilters.datesToShowFilterValue == "future"

                                    value = "future"
                                    +"Toekomstige schema"
                                }
                                taskDays.forEach { taskDay ->
                                    val dateValueStr = taskDay.date.format(DATE_FORMAT_ISO)
                                    option {
                                        selected = thuisprogrammaFilters.datesToShowFilterValue == dateValueStr

                                        value = dateValueStr
                                        +taskDay.date.format(DATE_TEXT_NO_YEAR_SHORT_DAY_FORMAT)
                                    }
                                }
                            }
                        }
                    ),
                    ToolbarItem(
                        itemHtmlId = "tp-team-toolbar-item",
                        buttonContent = {
                            icon("bi-people")
                            +"Team"
                        },
                        itemContent = {
                            select(classes = "form-select") {
                                id = "tp-filter-team-select"
                                onChangeFunction = { event ->
                                    val value = (event.currentTarget as HTMLSelectElement).value
                                    NavigationManager.setQueryParameters(mapOf("team" to value))
                                }
                                option {
                                    selected = thuisprogrammaFilters.team == null
                                    +"Alle teams"
                                }
                                thuisprogrammaDataManager.problemData.groupsDataFlow.value!!
                                    .forEach { team ->
                                        val teamSlug = team.name.toSlug()
                                        option {
                                            selected = thuisprogrammaFilters.team == teamSlug

                                            value = teamSlug
                                            +team.name
                                        }
                                    }
                            }
                        }
                    )
                )))

                val planningProblem = thuisprogrammaData.planningProblem
                val taskDaysToShow = thuisprogrammaFilters.filterTaskDaysToShow(thuisprogrammaData.taskDays)
                    .filter { taskDay -> taskDay.matches.any { thuisprogrammaFilters.showMatch(teamToFilter,it) && it.usePlanner } }

                taskDaysToShow
                    .forEach { taskDay ->
                        component(TaskDayViewComponent(
                            planningProblem,
                            taskDay,
                            taskAssigneeStoreData,
                            filtersState.value,
                            thuisprogrammaDataManager,
                            teamToFilter
                        ))
                }

                if (taskDaysToShow.isEmpty()) {
                    val team = teamsBySlug[thuisprogrammaFilters.team]
                    p {
                        +"Geen resultaten voor de combinatie "
                        strong { +thuisprogrammaFilters.getDatesToShowLabel().lowercase() }
                        +" en "
                        strong { +(team?.name ?: thuisprogrammaFilters.team ?: "alle teams") }
                    }
                }
            }
        }
    }

    override suspend fun getState(): StateFlow<*> = listOf(
        thuisprogrammaDataManager.problemData.getVersionData(problemVersionSlug).assigneeStoresState,
        thuisprogrammaDataManager.thuisprogrammaFiltersState,
        thuisprogrammaDataManager.problemData.groupsDataFlow.subscribe()
    ).combineAndLaunch(CoroutineManager.RENDER_SCOPE)
}

abstract class AbstractThuisprogrammaComponent(
    val problemSlug: String,
    val thuisprogrammaDataManager: ThuisprogrammaDataManager
) : HTMLComponent()

class AnonymousApiContext(scope: CoroutineScope, token: String) {
    val tokenLoader = VolleyPlannerAnonymousTokenLoader(token)
    val rootClient = VolleyplannerApiClientFactory.createVolleyplannerApiRootClient(tokenLoader)

    val anonymousVerificationFailed = MutableStateFlow<Boolean?>(null)
    val anonymousTokenResult = flow {
        try {
            val result = rootClient.verifyAnonymous()
            anonymousVerificationFailed.value = false
            emit(result)
        } catch (e: Exception) {
            anonymousVerificationFailed.value = true
        }
    }.stateIn(scope, SharingStarted.Eagerly, null)
    val organisationContext = anonymousTokenResult
        .filterNotNull()
        .distinctUntilChanged()
        .map { tokenResult ->
            // TODO: Introduce a GET organisation API (by slug)
            val organisation = rootClient.getOrganisations()
                .first { it.slug == tokenResult.organisationSlug }
            OrganisationContext(organisation, scope, tokenLoader)
        }.stateIn(scope, SharingStarted.Eagerly, null)
}

class AnonymousThuisProgrammaController : RootPageController() {
    private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.AnonymousThuisProgrammaController")

    override val name = "AnonymousThuisProgrammaController"
    override val title = "Thuisprogramma"
    override val path = "/thuisprogramma"
    // TODO: Make this work
    // additionalPaths: listOf("/thuisprogramma/:taskDay")
    override val requiredAuthLevel = AuthLevel.BasicUser
    override val childPages: List<WebPageController> = emptyList()

    override fun createRuntimeInstance() = AnonymousThuisProgrammaController()

    private val anonymousApiContextFlow = MutableStateFlow<AnonymousApiContext?>(null)
    @OptIn(ExperimentalCoroutinesApi::class)
    private val dataManagerFlow = anonymousApiContextFlow
        .filterNotNull()
        .distinctUntilChanged()
        .flatMapLatest { anonymousApiContext ->
            combine(
                anonymousApiContext.anonymousTokenResult,
                anonymousApiContext.organisationContext
            ) { anonymousTokenResult, organisationContext ->
                anonymousTokenResult to organisationContext
            }
        }
        .flatMapLatest { (anonymousTokenResult, organisationContext) ->
            flow {
                if (anonymousTokenResult == null || organisationContext == null) { emit(null) }
                else {
                    val dataManagerScope = CoroutineManager.createChildScope(CoroutineManager.CONTROLLER_SCOPE)
                    val dataManager = ThuisprogrammaDataManager(
                        anonymousTokenResult.pageType,
                        MutableStateFlow(ThuisprogrammaControls(false, false, false)),
                        "future",
                        organisationContext,
                        dataManagerScope
                    )
                    try {
                        emit(dataManager)
                        awaitCancellation()
                    } finally {
                        logger.info { " Cancelling dataManagerScope" }
                        dataManagerScope.cancel()
                    }
                }
            }
        }.stateIn(CoroutineManager.CONTROLLER_SCOPE, SharingStarted.Eagerly, null)

    @OptIn(ExperimentalCoroutinesApi::class)
    private val repaintJob = combine(
        dataManagerFlow,
        anonymousApiContextFlow.flatMapLatest { anonymousApiContext ->
            anonymousApiContext?.anonymousVerificationFailed ?: flowOf(null)
        }
    ) { dataManager, verificationFailed -> dataManager to verificationFailed }
        .distinctUntilChanged()
        .onEach { repaint() }
        .launchIn(CoroutineManager.CONTROLLER_SCOPE)

    override suspend fun mount(context: PageMountContext) {
        super.mount(context)
        val token = context.pageRouteContext.pathParameters.getValue("token")
        val anonymousApiContext = AnonymousApiContext(CoroutineManager.CONTROLLER_SCOPE, token)
        anonymousApiContextFlow.value = anonymousApiContext
    }

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            if (anonymousApiContextFlow.value != null && anonymousApiContextFlow.value!!.anonymousVerificationFailed.value == true) {
                // Show failed
                h1 { +"Geen toegang" }
            } else {
                if (dataManagerFlow.value == null) { spinner() }
                else {
                    component(ThuisProgrammaViewerComponent(dataManagerFlow.value!!))
                }
            }
        }
    }
}
